// Windowing: making every figure on the page describe the SAME span the trend
// chart shows (KET-6591). Before this, only the chart followed Range/Period and
// the headline, the four figures and the campaign table silently stayed on the
// producer's latest closed month - two claims about two different periods on
// one screen.
//
// Nothing here invents a figure (I-4). The producer ships additive totals on
// every series point and every per-(bucket, campaign) row; a window is their
// SUM, and the only division is the one the KPI itself defines (profit / spend,
// exact-margin sales / sales). A ratio is never averaged across buckets - that
// would weight a GBP 5 week like a GBP 5,000 one.
//
// Pure functions only - no query, no fetch, no React.

import { rangeMonths, trimPartialTrailing, type GrainValue, type RangeValue } from "@/lib/time-range";

import { PROVISIONAL_TEXT, isProvisionalMonth } from "./format";
import type { CampaignRow, GrainCampaignRow, SeriesPoint, Tail, Tile, Verdict } from "./api";

export type WindowSummary = {
  /** Human label for the span, e.g. "Jun 2026 to Aug 2026" or "27 Jun to 24 Sep". */
  label: string;
  /** Bucket keys the summary covers, oldest first. */
  buckets: string[];
  profit_gbp: number | null;
  spend_gbp: number | null;
  profit_per_pound: number | null;
  attributed_sales_gbp: number | null;
  attributed_units: number | null;
  coverage_pct: number | null;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function formatBucket(bucket: string, grain: GrainValue): string {
  const [year, month, day] = bucket.split("-");
  const monthName = MONTHS[Number(month) - 1];
  if (!monthName) return bucket;
  if (grain === "monthly" || day === undefined) return `${monthName} ${year}`;
  return `${Number(day)} ${monthName}`;
}

export function windowLabel(grain: GrainValue, buckets: string[]): string {
  if (buckets.length === 0) return "";
  const first = formatBucket(buckets[0], grain);
  const last = formatBucket(buckets[buckets.length - 1], grain);
  if (grain === "weekly" && buckets.length === 1) return `week of ${last}`;
  return first === last ? last : `${first} to ${last}`;
}

/** Sum one field over points; `null` when ANY point lacks it (an unknown is not a zero). */
function sumField(points: SeriesPoint[], field: keyof SeriesPoint): number | null {
  let total = 0;
  for (const point of points) {
    const value = point[field];
    if (typeof value !== "number") return null;
    total += value;
  }
  return total;
}

/**
 * Whether these points carry the producer's additive totals at all. An artefact
 * written before the producer shipped them has ratio-only points, and the page
 * must fall back to the producer's own figures rather than state a sum of nothing.
 */
export function hasTotals(points: SeriesPoint[]): boolean {
  return points.length > 0 && points.every((p) => typeof p.profit_gbp === "number" && typeof p.spend_gbp === "number");
}

/**
 * The window's totals, summed over exactly the points the chart plots.
 *
 * The monthly rollup has no units or exact-margin sales. Both are raw
 * attribution facts that do not depend on the margin fallback, so for a monthly
 * window they are summed from the daily points inside those months - exact, not
 * approximated - provided the daily series reaches back to the first month.
 * When it does not, units and coverage are `null` and render as "n/a".
 */
export function summarizeWindow(
  points: SeriesPoint[],
  grain: GrainValue,
  daily: SeriesPoint[] = [],
): WindowSummary {
  const buckets = points.map((p) => p.m);
  const profit = sumField(points, "profit_gbp");
  const spend = sumField(points, "spend_gbp");

  let sales = sumField(points, "attributed_sales_gbp");
  let units = sumField(points, "attributed_units");
  let exact = sumField(points, "exact_sales_gbp");

  if (grain === "monthly" && points.length > 0 && (units === null || exact === null)) {
    const months = new Set(buckets);
    const presentDaily = daily.filter((p) => p.present);
    const firstDaily = presentDaily[0]?.m;
    const reaches = firstDaily !== undefined && firstDaily <= `${buckets[0]}-01`;
    const inside = presentDaily.filter((p) => months.has(p.m.slice(0, 7)));
    if (reaches && inside.length > 0) {
      const dailySales = sumField(inside, "attributed_sales_gbp");
      units = sumField(inside, "attributed_units");
      exact = sumField(inside, "exact_sales_gbp");
      // Coverage is exact-margin sales over the SAME base it was counted on.
      if (exact !== null && dailySales !== null) {
        sales = sales ?? dailySales;
        return finish(buckets, grain, profit, spend, sales, units, dailySales === 0 ? null : (exact * 100) / dailySales);
      }
    } else {
      units = null;
      exact = null;
    }
  }

  const coverage = exact === null || sales === null || sales === 0 ? null : (exact * 100) / sales;
  return finish(buckets, grain, profit, spend, sales, units, coverage);
}

function finish(
  buckets: string[],
  grain: GrainValue,
  profit: number | null,
  spend: number | null,
  sales: number | null,
  units: number | null,
  coverage: number | null,
): WindowSummary {
  return {
    label: windowLabel(grain, buckets),
    buckets,
    profit_gbp: profit === null ? null : round2(profit),
    spend_gbp: spend === null ? null : round2(spend),
    profit_per_pound: profit === null || spend === null || spend === 0 ? null : round2(profit / spend),
    attributed_sales_gbp: sales === null ? null : round2(sales),
    attributed_units: units,
    coverage_pct: coverage === null ? null : round2(coverage),
  };
}

// --- The campaign table, windowed (ported from Meteoric's E3) ---------------

// Mirror of `marketing_analytics/profit_yield/verdict.py` thresholds - kept in
// lockstep with that file (SPEND_FLOOR=25, SCALE_SPEND_FLOOR=50,
// SCALE_PROFIT_PER_POUND=0.30). The monthly-closed maturity gate is dropped on
// purpose: on a daily/weekly window the window itself is the unit of maturity
// (Damien decision 2026-06-27, carried over from Meteoric).
const WINDOW_SPEND_FLOOR = 25;
const WINDOW_SCALE_SPEND_FLOOR = 50;
const WINDOW_SCALE_PROFIT_PER_POUND = 0.3;
const TOP_CAMPAIGNS = 25;

export function verdictForWindow(spendGbp: number, profitPerPound: number | null): Verdict {
  if (spendGbp < WINDOW_SPEND_FLOOR) return "insufficient-data";
  if (profitPerPound !== null && profitPerPound < 0) return "cut";
  if (profitPerPound !== null && profitPerPound >= WINDOW_SCALE_PROFIT_PER_POUND && spendGbp >= WINDOW_SCALE_SPEND_FLOOR) {
    return "scale";
  }
  return "hold";
}

/**
 * The bucket keys inside the window, bounded by CALENDAR date, not bucket count:
 * campaign buckets are sparse (a campaign has a bucket only on days it spent),
 * so "the last 30 buckets" could reach months back. `months`, when given,
 * restricts daily rows to those calendar months - how a monthly window reads
 * the daily campaign rows.
 */
export function windowBucketSet(
  rows: GrainCampaignRow[],
  grain: GrainValue,
  range: RangeValue,
  reference: Date,
  months?: Set<string>,
): string[] {
  const unique = [...new Set(rows.map((row) => row.bucket))].sort();
  if (months) return unique.filter((bucket) => months.has(bucket.slice(0, 7)));
  const trimmed = trimPartialTrailing(unique.map((m) => ({ m })), grain, reference).map((p) => p.m);
  if (range === "all") return trimmed;
  const cutoff = new Date(reference);
  cutoff.setUTCDate(cutoff.getUTCDate() - Number(range));
  const cutoffKey = cutoff.toISOString().slice(0, 10);
  return trimmed.filter((m) => m >= cutoffKey);
}

export type WindowedCampaigns = {
  campaigns: CampaignRow[];
  tail: Tail;
  /** False when the grain has no rows yet - the table says so rather than going blank. */
  available: boolean;
};

export function windowCampaigns(rows: GrainCampaignRow[], buckets: string[]): WindowedCampaigns {
  const allowed = new Set(buckets);
  type Agg = { row: CampaignRow; spend: number; sales: number; units: number; profit: number; exact: number };
  const byKey = new Map<string, Agg>();

  for (const row of rows) {
    if (!allowed.has(row.bucket)) continue;
    const key = `${row.campaign_id}|${row.ad_product}|${row.marketplace}`;
    let agg = byKey.get(key);
    if (!agg) {
      agg = {
        row: {
          campaign_id: row.campaign_id,
          name: row.name,
          ad_product: row.ad_product,
          marketplace: row.marketplace,
          product: row.product,
          variant: row.variant,
          variant_count: row.variant_count,
          spend_gbp: 0,
          attr_sales_gbp: 0,
          attr_units: 0,
          profit_gbp: 0,
          profit_per_pound: null,
          coverage_pct: null,
          verdict: "insufficient-data",
        },
        spend: 0,
        sales: 0,
        units: 0,
        profit: 0,
        exact: 0,
      };
      byKey.set(key, agg);
    }
    agg.spend += row.spend_gbp ?? 0;
    agg.sales += row.attr_sales_gbp ?? 0;
    agg.units += row.attr_units ?? 0;
    agg.profit += row.profit_gbp ?? 0;
    agg.exact += row.exact_sales_gbp ?? 0;
  }

  const all: CampaignRow[] = [...byKey.values()].map((agg) => {
    const ppp = agg.spend === 0 ? null : round2(agg.profit / agg.spend);
    return {
      ...agg.row,
      spend_gbp: round2(agg.spend),
      attr_sales_gbp: round2(agg.sales),
      attr_units: agg.units,
      profit_gbp: round2(agg.profit),
      profit_per_pound: ppp,
      coverage_pct: agg.sales === 0 ? null : round2((agg.exact * 100) / agg.sales),
      verdict: verdictForWindow(agg.spend, ppp),
    };
  });
  all.sort((a, b) => (b.spend_gbp ?? 0) - (a.spend_gbp ?? 0));

  const tailRows = all.slice(TOP_CAMPAIGNS);
  return {
    campaigns: all.slice(0, TOP_CAMPAIGNS),
    tail: {
      count: tailRows.length,
      spend_gbp: tailRows.length === 0 ? null : round2(tailRows.reduce((sum, r) => sum + (r.spend_gbp ?? 0), 0)),
      profit_gbp: tailRows.length === 0 ? null : round2(tailRows.reduce((sum, r) => sum + (r.profit_gbp ?? 0), 0)),
    },
    available: buckets.length > 0 && all.length > 0,
  };
}

/**
 * What the headline should state for the plotted points.
 *
 * - The producer's own closed month (Monthly, one point) keeps the producer's
 *   figures whole - band, delta and maturity included.
 * - Any other window with totals becomes that window's sums.
 * - Points without totals (an artefact from before the producer shipped them)
 *   or an empty window keep the producer's month, and SAY so - never a figure
 *   that silently describes a different span from the chart beside it.
 */
export function headlineWindow(
  tile: Pick<Tile, "period" | "series_daily">,
  points: SeriesPoint[],
  grain: GrainValue,
  range: RangeValue,
  reference?: Date,
): { window: WindowSummary | null; note?: string } {
  const month = tile.period ?? "the latest closed month";
  // A one-month monthly view IS the KPI's own month. The monthly rollup can lag
  // the live headline (it had no August rows on 2026-09-26 while the tile
  // computed August), so the producer's figure - band and delta included -
  // wins over summing whatever month the rollup last reached.
  if (grain === "monthly" && rangeMonths(range) === 1) return { window: null };
  if (points.length === 0) return { window: null, note: `No data in the selected range, so these figures show ${month}.` };
  if (!hasTotals(points)) {
    return {
      window: null,
      note: `These figures show ${month}. Totals for the selected range arrive with the next daily refresh.`,
    };
  }
  const window = summarizeWindow(points, grain, tile.series_daily ?? []);
  // A provisional month summed into the window is stated beside the figures,
  // not only on the chart (bug 3f30402f).
  const provisional = grain === "monthly" ? points.filter((p) => isProvisionalMonth(p, reference)).map((p) => p.m) : [];
  return provisional.length > 0
    ? { window, note: `Includes ${provisional.join(" and ")}: ${PROVISIONAL_TEXT.toLowerCase()}.` }
    : { window };
}
